import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { InspectionDto, Order } from "@/types";
import Accordion from "@/components/organisms/AccordionDrawer";
import Text from "@/components/atoms/Text";
import Alert from "@/components/atoms/Alert";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import OrderStatusEnum from "@/constants/enums/OrderStatusEnum";
import { formatNumber } from "@/components/organisms/InspectionModal/utils";
import { OrderInspectionView } from "../../useOrderInspectionView";
import { formatDateTime, InspectionPhotos, Row, UserName } from "./TabParts";
import DeviationProductsAccordion from "./DeviationProductsAccordion";
import DeviationServicesAccordion from "./DeviationServicesAccordion";
import DeviationCostsAccordion from "./DeviationCostsAccordion";

interface InspectionTabProps {
  order: Order;
  view: OrderInspectionView;
}

/**
 * The receiving inspection with its deviations, read-only. The seller sees the weights, the time, the photos and the
 * deviations without who decided them; the buyer sees it all.
 */
const InspectionTab = ({ order, view }: InspectionTabProps) => {
  const { t } = useTranslation();

  if (view.kind === "awaiting") {
    return (
      <Accordion initiallyOpen title={t("inspections:drawer.tab")}>
        <Grid p={20}>
          <Grid.Col span={12}>
            <Text size="sm" data-testid="inspection-awaiting">
              {view.status === OrderStatusEnum.DEVIATION_PENDING
                ? t("inspections:drawer.awaitingDeviation")
                : t("inspections:drawer.awaitingInspection")}
            </Text>
          </Grid.Col>
        </Grid>
      </Accordion>
    );
  }

  if (view.kind === "none") return null;

  const { inspection } = view;
  const isBuyer = order.orderType.id === OrderTypeEnum.BUY;
  const products = inspection.deviations.filter((deviation) => deviation.kind === "PRODUCT");
  const services = inspection.deviations.filter((deviation) => deviation.kind === "SERVICE");

  return (
    <>
      {order.status === OrderStatusEnum.DEVIATION_PENDING && (
        <Alert color="warning" m={20} data-testid="inspection-deviation-pending">
          {t("inspections:drawer.awaitingDeviation")}
        </Alert>
      )}
      <WeightAccordion inspection={inspection} prefillWeightKg={view.prefillWeightKg} />
      <ReceiptAccordion inspection={inspection} isBuyer={isBuyer} />
      {products.length > 0 && <DeviationProductsAccordion deviations={products} isBuyer={isBuyer} />}
      {services.length > 0 && <DeviationServicesAccordion deviations={services} isBuyer={isBuyer} />}
      {inspection.deviations.length > 0 && <DeviationCostsAccordion inspection={inspection} />}
    </>
  );
};

export default InspectionTab;

/** The inspected weight against the expected one. The deviation confirmation task shows it too. */
export function WeightAccordion({
  inspection,
  prefillWeightKg,
}: {
  inspection: InspectionDto;
  prefillWeightKg: number | null;
}) {
  const { t, i18n } = useTranslation();
  const kg = (value: number, signed = false) => `${formatNumber(value, i18n.language, { signed })} kg`;

  const differenceKg = prefillWeightKg != null ? inspection.finalWeightKg - prefillWeightKg : null;
  // A percentage of nothing means nothing, so an expected weight of 0 shows kilos only.
  const differencePercent =
    differenceKg != null && prefillWeightKg
      ? ` (${formatNumber((differenceKg / prefillWeightKg) * 100, i18n.language, { decimals: 1, signed: true })} %)`
      : "";

  return (
    <Accordion initiallyOpen title={t("inspections:drawer.weightTitle")}>
      <Grid p={20}>
        {prefillWeightKg != null && <Row label={t("inspections:drawer.expectedWeight")}>{kg(prefillWeightKg)}</Row>}
        <Row label={t("inspections:drawer.finalWeight")}>{kg(inspection.finalWeightKg)}</Row>
        {differenceKg != null && (
          <Row label={t("inspections:drawer.difference")}>
            <Text size="sm" data-testid="inspection-difference">{`${kg(differenceKg, true)}${differencePercent}`}</Text>
          </Row>
        )}
      </Grid>
    </Accordion>
  );
}

/** When and by whom the delivery was inspected, with its photos. The deviation confirmation task shows it too. */
export function ReceiptAccordion({ inspection, isBuyer }: { inspection: InspectionDto; isBuyer: boolean }) {
  const { t } = useTranslation();

  return (
    <Accordion initiallyOpen title={t("inspections:drawer.receiptTitle")}>
      <Grid p={20}>
        <Row label={t("inspections:drawer.inspectedAt")}>{formatDateTime(inspection.inspectedAt)}</Row>
        {isBuyer && (
          <>
            <Row label={t("inspections:drawer.inspectedBy")}>
              <UserName userId={inspection.inspectedByUserId} />
            </Row>
            <Row label={t("inspections:weighing.registrationNumber")}>
              {inspection.transportRegistrationNumber || "-"}
            </Row>
            <Row label={t("inspections:weighing.poNumber")}>{inspection.poNumber || "-"}</Row>
            <Row label={t("inspections:drawer.comment")}>{inspection.comment || "-"}</Row>
          </>
        )}
        <Row label={t("inspections:photos.label")}>
          <InspectionPhotos photoIds={inspection.photoIds} />
        </Row>
      </Grid>
    </Accordion>
  );
}
